import { Loading } from '@monorepo/shared/componentsV2/Loading';
import { useSearchParamState } from '@monorepo/shared/hooks/navigation/useSearchParamState';
import { useHasProjectSettingsPermissions } from '@monorepo/shared/hooks/permissions/useHasPermissions';
import { isOrganizationScopedFolder } from '@monorepo/shared/utils/documentFolderScope';
import React, { useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useSiteDocumentsLink } from '../../hooks/useSiteDocumentsLinks';
import { useSiteDocumentsList } from '../../hooks/useSiteDocumentsList';
import { Page } from '../styled';
import { SiteFolderDocumentsHeader } from './SiteFolderDocumentsHeader';
import { SiteFolderDocumentsTable } from './SiteFolderDocumentsTable';

export function SiteFolderDocumentsPage() {
  const { organizationId, projectId, folderId } = useParams();
  if (!organizationId || !projectId || !folderId) {
    throw new Error(
      "SiteFolderDocuments page was rendered with a url that doesn't have organizationId, projectId, and folderId",
    );
  }

  const { siteDocuments, isLoading } = useSiteDocumentsList({
    organizationId,
    projectId,
  });
  const folder = useMemo(
    () => siteDocuments?.folders.find((f) => f.id === folderId),
    [siteDocuments, folderId],
  );

  const documentsLink = useSiteDocumentsLink();
  const canEditSiteDocuments = useHasProjectSettingsPermissions(projectId);

  const [filter, setFilter] = useSearchParamState<string>('filter');
  const nonNullFilter = filter ?? '';

  if (isLoading) {
    return <Loading />;
  }

  if (!folder) {
    return <Navigate to={documentsLink} replace />;
  }

  return (
    <Page>
      <SiteFolderDocumentsHeader
        organizationId={organizationId}
        projectId={projectId}
        folder={folder}
        filter={nonNullFilter}
        onSearch={setFilter}
        canEditSiteDocuments={canEditSiteDocuments}
      />
      <SiteFolderDocumentsTable
        organizationId={organizationId}
        projectId={projectId}
        folderId={folder.id}
        isOrganizationScopedFolder={isOrganizationScopedFolder(folder.scope)}
        filter={nonNullFilter}
        canEditSiteDocuments={canEditSiteDocuments}
      />
    </Page>
  );
}
